import React from "react";
import PropTypes from "prop-types";

import Dropdown from "./navigation/Dropdown";
import Buttons from "./navigation/Buttons";
import FooterButtons from "./navigation/FooterButtons";
import TabTitle  from "./TabTitle";
import KeyNumbers from "./keyNumbers";
import Tariffs from "./tariffs";
import ODAIndex from "./oda";

import mapData from "./data/mapData";
import { defaultMapOptions, tariffMapColorAxis } from "./map/options";

// data helper functions
const getKeyNumbersMapData = data => {
  return data.map(country => {
    const hasWdProfile = country.has_wd_profile;
    const hasSnapshot = country.has_snapshot;
    // hasSnapshotOrProfile will color countries according to 'from' and 'to' values in
    // this.state.keyNumbersMapOptions.colorAxis.dataClasses
    // if a country has neither this value is null and thus given no color and is unclickable
    let hasSnapshotOrProfile = null;
    if(hasWdProfile) {
      hasSnapshotOrProfile = 1;
    }
    else if (hasSnapshot) {
      hasSnapshotOrProfile = 0.5;
    }
    return [country.country_id, hasSnapshotOrProfile];
  });
};

const getTariffMapData = data => {
  /*
    [
      ['country_id', tariff_average_value],
    ]
  */
  return data.map(country => [country.country_id, country.tariff_average_value]);
};

const getOdaMapData = data => {
  /*
    [
      ['country_id', bool], => has oda_numbers object
      ['country_id', null], => empty / no oda_numbers object
    ]
  */
  const valuesArray = data.map(country => {
    const hasATD = country.oda_numbers.annual_total_disbursement.length !== 0;
    const hasBBD = country.oda_numbers.breakdown_by_donor.length !== 0;
    return [country.country_id, (hasATD || hasBBD || null)];
  });
  return valuesArray;
};

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedCountry: "all",
      keyNumbers: {},
      tariffNumbers: [],
      seychellesTariffValue: 0,
      caboVerdeTariffValue: 0,
      mauritiusTariffValue: 0,
      odaNumbers: {},
      currentTab: "keyNumbers",
      keyNumbersMapOptions: {
        ...defaultMapOptions,
        legend: {
          enabled: false
        },
        colorAxis: {
          dataClasses: [{
            from: 0.1,
            to: 0.99,
            color: "#b9e1f8",
            name: "Countries with snapshot"
          }, {
            from: 1,
            to: 2,
            color: "#3585b9",
            name: "Countries with WaterData profile"
          }]
        },
        plotOptions: {
          series: {
            point: {
              events: {
                click: (function(component) {
                  return function() {
                    if (component.state.selectedCountry === event.point["hc-key"]) {
                      return false;
                    }
                    component.selectFromKeyNumbersMap(event.point["hc-key"]);
                  };
                })(this)
              }
            }
          }
        },
        series: [{
          mapData: mapData,
          data: getKeyNumbersMapData(props.data.africa_region_page_map),
          allowPointSelect: true,
          nullColor: "#bdc2bf",
          borderColor: "#fff",
          states: {
            select: {
              color: "#c84646",
              borderColor: "#fff"
            },
            hover: {
              brightness: 0.1
            }
          }
        }]
      },
      tariffMapOptions: {
        ...defaultMapOptions,
        legend: {
          enabled: false
        },
        colorAxis: {
          dataClasses: tariffMapColorAxis
        },
        plotOptions: {
          series: {
            events: {
              click: (function(component) {
                return function() {
                  if (component.state.selectedCountry === event.point["hc-key"]) {
                    return false;
                  }
                  component.selectFromTariffMap(event.point["hc-key"]);
                };
              })(this)
            }
          }
        },
        series: [{
          mapData: mapData,
          data: getTariffMapData(props.data.africa_region_page_map),
          allowPointSelect: true,
          nullColor: "#bdc2bf",
          borderColor: "#fff",
          states: {
            select: {
              color: "#c84646",
              borderColor: "#fff"
            },
            hover: {
              brightness: 0.1
            }
          }
        }]
      },
      odaMapOptions: {
        ...defaultMapOptions,
        legend: {
          enabled: false
        },
        plotOptions: {
          series: {
            events: {
              click: (function(component) {
                return function() {
                  if (component.state.selectedCountry === event.point["hc-key"]) {
                    return false;
                  }
                  component.selectFromODAMap(event.point["hc-key"]);
                };
              })(this)
            }
          }
        },
        series: [{
          mapData: mapData,
          data: getOdaMapData(props.data.africa_region_page_map),
          allowPointSelect: true,
          nullColor: "#bdc2bf",
          borderColor: "#fff",
          states: {
            select: {
              color: "#c84646"
            },
            hover: {
              color: "#3785b8",
              brightness: 0
            }
          }
        }],
      },
    };

    this.onChange = this.onChange.bind(this);
    this.selectFromKeyNumbersMap = this.selectFromKeyNumbersMap.bind(this);
    this.selectFromODAMap = this.selectFromODAMap.bind(this);
    this.selectFromTariffMap = this.selectFromTariffMap.bind(this);
    this.changeTab = this.changeTab.bind(this);
    this.resetCountry = this.resetCountry.bind(this);
  }

  componentDidMount() {
    // set some state values we can use to color the separate island maps according to their tariff values
    // on the tariffs tab
    const seychellesTariffValue = this.props.data.africa_region_page_map.find(country => country.country_id === "sc").tariff_average_value;
    const mauritiusTariffValue = this.props.data.africa_region_page_map.find(country => country.country_id === "mu").tariff_average_value;
    const caboVerdeTariffValue = this.props.data.africa_region_page_map.find(country => country.country_id === "cv").tariff_average_value;

    this.setState({
      seychellesTariffValue,
      caboVerdeTariffValue,
      mauritiusTariffValue
    });
  }

  onChange(e) {
    if (e !== "all") {
      const countryData = this.props.data.africa_region_page_map.find(country => country.country_id === e);
      this.setState({
        selectedCountry: e,
        keyNumbers: countryData && countryData.key_numbers,
        tariffNumbers: countryData && countryData.tariffs,
        odaNumbers: countryData && countryData.oda_numbers
      });
    } else {
      this.setState({
        selectedCountry: "all"
      });
    }
  }

  selectFromKeyNumbersMap(e) {
    const countryData = this.props.data.africa_region_page_map.find(country => country.country_id === e);
    this.setState({
      selectedCountry: e,
      keyNumbers: countryData.key_numbers
    });
  }

  selectFromTariffMap(e) {
    const countryData = this.props.data.africa_region_page_map.find(country => country.country_id === e);
    this.setState({
      selectedCountry: e,
      tariffNumbers: countryData.tariffs,
    });
  }

  selectFromODAMap(e) {
    const countryData = this.props.data.africa_region_page_map.find(country => country.country_id === e);
    this.setState({
      selectedCountry: e,
      odaNumbers: countryData.oda_numbers
    });
  }

  changeTab(tab) {
    const { selectedCountry } = this.state;
    const countryData = selectedCountry !== "all" &&
      this.props.data.africa_region_page_map.find(country => country.country_id === selectedCountry);
    if(selectedCountry !== "all" && tab === "keyNumbers" && (countryData.has_snapshot || countryData.has_wd_profile)) {
      this.setState({
        currentTab: tab,
        keyNumbers: countryData.key_numbers,
      });
    } else if (selectedCountry !== "all" && tab === "tariffs" && countryData.tariff_average_value !== null) {
      this.setState({
        currentTab: tab,
        tariffNumbers: countryData.tariffs,
      });
    } else if (selectedCountry !== "all" &&
                tab === "oda" &&
                (countryData.oda_numbers.annual_total_disbursement.length !== 0 ||
                countryData.oda_numbers.breakdown_by_donor.length !== 0)
              ) {
      this.setState({
        currentTab: tab,
        odaNumbers: countryData.oda_numbers,
      });
    } else {
      this.setState({
        currentTab: tab,
        selectedCountry: "all",
      });
    }
  }

  resetCountry() {
    this.setState({
      selectedCountry: "all"
    });
  }

  render() {
    const {
      selectedCountry,
      currentTab,
      keyNumbers,
      keyNumbersMapOptions,
      tariffMapOptions,
      tariffNumbers,
      odaMapOptions,
      odaNumbers,
      seychellesTariffValue,
      caboVerdeTariffValue,
      mauritiusTariffValue
    } = this.state;

    const {
      africa_region_page_map,
      overall_oda_data,
      card_id,
      current_year,
    } = this.props.data;

    return (
      <div className="interactive-africa-map">
        <div className="navigation">
          <div className="country-select-wrapper">
            <Dropdown
              options={africa_region_page_map}
              onChange={this.onChange}
              selected={selectedCountry}
              currentTab={currentTab}
            />
            <div onClick={this.resetCountry} className="reset-link">
              {selectedCountry !== "all" && <span>See all</span>}
            </div>
          </div>
          <Buttons onClick={this.changeTab} active={currentTab} />
        </div>
        <div className="tab-title">
          <TabTitle
            data={africa_region_page_map}
            currentTab={currentTab}
            selectedCountry={selectedCountry}
          />
        </div>
        {currentTab === "keyNumbers" &&
          <KeyNumbers
            mapOptions={keyNumbersMapOptions}
            selectedCountry={selectedCountry}
            data={keyNumbers}
            icons={this.props.icons}
            currentTab={currentTab}
          />
        }
        {currentTab === "tariffs" &&
          <Tariffs
            mapOptions={tariffMapOptions}
            selectedCountry={selectedCountry}
            data={tariffNumbers}
            currentYear={current_year}
            selectCountry={this.selectFromTariffMap}
            seychellesTariffValue={seychellesTariffValue}
            mauritiusTariffValue={mauritiusTariffValue}
            caboVerdeTariffValue={caboVerdeTariffValue}
            currentTab={currentTab}
          />
        }
        {currentTab === "oda" &&
          <ODAIndex
            mapOptions={odaMapOptions}
            selectedCountry={selectedCountry}
            data={odaNumbers}
            overallOdaData={overall_oda_data}
          />
        }
        <FooterButtons
          selectedCountry={selectedCountry}
          currentTab={currentTab}
          countries={africa_region_page_map}
          cardId={card_id}
        />
      </div>
    );
  }
}

export default App;

App.propTypes = {
  data: PropTypes.shape({
    africa_region_page_map: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    card_id: PropTypes.number.isRequired,
    current_year: PropTypes.number.isRequired,
    overall_oda_data: PropTypes.shape({}).isRequired,
  }).isRequired,
  icons: PropTypes.shape({}).isRequired,
};
